/** @jsxImportSource @emotion/react */

import React from "react";
import Immutable from "immutable";
import {css, keyframes} from "@emotion/react";

import Drawer from "js/common/views/drawer";
import * as Branding from "js/common/branding-constants";
import {IconButton, TextButton} from "js/common/views/inputs/buttons";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import AiAssistantStartOverDialog from "js/dashboards/ai-assistant/ai-assistant-startover-dialog";
import * as Users from "js/common/users";
import * as Auditor from "js/common/auditor";
import AiAssistantPreview from "js/dashboards/ai-assistant/ai-assistant-preview";
import {white, black, bullhornLightOrange, lightestGrey} from "js/common/cube19-colors";

const bounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
`;
const chatContainer = css`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

const chatContent = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: flex-end;
  min-height: 100%;
`;

const messageContainer = css`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const avatar = (theme) => css`
  width: 32px;
  height: 32px;
  background-color: ${theme.palette.primary.main};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${theme.themeId === "light" ? "#fff" : "#000"};
`;

const userAvatar = (theme) => css`
  width: 32px;
  height: 32px;
  background-color: ${bullhornLightOrange};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 12px;
  color: ${theme.themeId === "light" ? "#fff" : "#000"};
`;

const assistantName = (theme) => css`
  font-size: 14px;
  font-weight: 500;
  color: ${theme.palette.text.main};
  margin-bottom: 4px;
`;

const messageText = (theme) => css`
  font-size: 14px;
  color: ${theme.palette.text.main};
  line-height: 1.5;
  background-color: ${theme.palette.background.paper};
  padding: 14px 14px 10px 14px;
  max-width: 100%;
  border-radius: 8px;
  width: max-content;
`;

const examplePrompt = (theme) => css`
  background-color: ${theme.palette.background.card};
  border: 1px solid ${theme.palette.background.paper};
  font-size: 14px;
  color: ${theme.palette.text.main};
  padding: 12px;
  margin-bottom: 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  border: 1px solid ${theme.palette.border.light};
`;

const userMessage = (theme) => css`
  background-color: ${theme.themeId === "light" ? lightestGrey : theme.palette.background.paper};
  color: ${theme.palette.text.main};
  font-size: 14px;
  border-radius: 8px;
  padding: 14px 14px 10px 14px;
  max-width: 100%;
`;

const typingDot = (theme) => css`
  width: 8px;
  height: 8px;
  background-color: ${theme.palette.text.main};
  border-radius: 50%;
  animation: ${bounce} 1.4s infinite ease-in-out both;
`;

const inputSection = css`
  border-top: 1px solid #e0e0e0;
  padding: 16px;
`;

const inputContainer = css`
  display: flex;
  align-items: center;
  position: relative;
`;

const textarea = (theme) => css`
  width: 100%;
  resize: none;
  background: ${theme.themeId === "light" ? theme.palette.background.card : theme.palette.background.paper};
  border: 1px solid ${theme.palette.border.light};
  color: ${theme.palette.text.main};
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  min-height: 50px;
  max-height: 120px;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${theme.palette.primary.main};
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
  }
`;

const sendButtonStyle = (theme) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.themeId === "light" ? white : black,
  borderRadius: 8,
  padding: 8,
  cursor: "pointer",
  height: 30,
  width: 30,
  position: "absolute",
  top: 10,
  right: 10
});

const AiAssistantDrawer = React.memo(
  ({
    open,
    onRequestClose,
    onRequestDialog,
    onStartOver,
    messages = Immutable.List(),
    onSendMessage,
    isLoading = false,
    dashboardConfig,
    queueDataLoad,
    clearDataLoad,
    onDashboardChange,
    dashboard,
    componentTypeToTemplate,
    activeTabId,
    dataTestId
  }) => {
    const {theme} = React.useContext(CustomThemeContext);
    const DRAWER_WIDTH = "auto";
    const [inputValue, setInputValue] = React.useState("");
    const chatContainerRef = React.useRef(null);

    React.useEffect(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTo({
          top: chatContainerRef.current.scrollHeight,
          behavior: "smooth"
        });
      }
    }, [messages, isLoading]);

    // Note: I've tried combing these, it just works better with 2 useEffects
    // This second one is so that we always scroll to the bottom when opening and closing the drawer
    // We use requestAnimationFrame as the ref won't exist at the point of opening
    React.useEffect(() => {
      if (open) {
        requestAnimationFrame(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTo({
              top: chatContainerRef.current.scrollHeight,
              behavior: "smooth"
            });
          }
        });
      }
    }, [open]);

    const titleIcon = <i className={"bhi-magic"} style={{paddingRight: "0.65rem", fontSize: "1.5rem"}} />;

    const resetButton = (
      <TextButton
        onClick={() => {
          const shouldShowWarning = localStorage.getItem("showAiAssistantStartOverWarning") !== "false";
          Auditor.audit("ai-assistant-reset", {from: "ai-assistant-drawer"});
          if (shouldShowWarning) {
            renderDialog(onRequestDialog);
          } else {
            onStartOver();
          }
        }}
        disabled={messages.count() < 1 || isLoading}
        icon="republish"
        iconType="bhi"
        label="Start Over"
        style={{
          float: "right",
          marginRight: "1rem",
          backgroundColor: theme.palette.background.paper
        }}
      />
    );

    const initialMessage = Immutable.Map({
      type: "assistant",
      content: `Hi, I am Bullhorn's ${Branding.aiAssistantName}. I'm here to help you find the right visuals to answer questions about your data. Start by telling me what you are looking for. For example:`,
      examples: [
        "Show me Total Sales for the last 12 months",
        "Who are my top clients this quarter?"
      ]
    });

    const renderDialog = (onRequestDialog) => {
      const title = "Are you sure you want to start over?";
      const closeDialog = onRequestDialog(
        <AiAssistantStartOverDialog
          onCancel={() => closeDialog()}
          onStartOver={() => {
            onStartOver();
            closeDialog();
          }}
        />,
        {title: title, width: 560, minHeight: 230}
      );
    };

    const handleSendMessage = () => {
      if (!inputValue.trim()) {
        return;
      }
      onSendMessage(inputValue.trim());
      setInputValue("");
    };

    const handleExampleClick = (example) => {
      setInputValue(example);
    };

    const currentUser = Users.getCurrentUser();
    const userInitials = `${currentUser.get("firstName")?.[0] || ""}${
      currentUser.get("lastName")?.[0] || ""
    }`.toUpperCase();
    const allMessages = Immutable.List([initialMessage]).concat(messages);
    const canSend = inputValue.trim() && !isLoading && inputValue.length >= 1;

    return (
      <Drawer
        dataProductId={open ? "ai-assistant-drawer-open" : "ai-assistant-drawer-closed"}
        dataTestId={dataTestId || "ai-assistant-drawer"}
        width={DRAWER_WIDTH}
        onRequestClose={onRequestClose}
        open={open}
        title={
          <>
            {titleIcon}
            {Branding.aiAssistantName}
            {resetButton}
          </>
        }
        showCloseButton
        disableRestoreFocus={true}
        openRight={true}
      >
        <div css={chatContainer} ref={chatContainerRef}>
          <div css={chatContent}>
            {allMessages.map((message, index) => {
              if (message.get("type") === "assistant") {
                return (
                  <div key={index} css={messageContainer}>
                    <div css={avatar(theme)}>
                      <i className="bhi-magic" />
                    </div>
                    <div>
                      <div css={assistantName(theme)}>{Branding.aiAssistantName}</div>
                      <div css={messageText(theme)}>
                        <div>{message.get("content")}</div>
                        {message.get("examples") && (
                          <div
                            style={{
                              marginTop: "12px",
                              display: "flex",
                              flexDirection: "column"
                            }}
                          >
                            {message.get("examples").map((example, exampleIndex) => (
                              <div
                                key={exampleIndex}
                                css={examplePrompt(theme)}
                                onClick={() => handleExampleClick(example)}
                              >
                                {example}
                              </div>
                            ))}
                          </div>
                        )}
                        {message.get("componentConfig") && (
                          <AiAssistantPreview
                            dashboardConfig={dashboardConfig}
                            componentConfig={message.get("componentConfig")}
                            onRequestDialog={onRequestDialog}
                            queueDataLoad={queueDataLoad}
                            clearDataLoad={clearDataLoad}
                            onDashboardChange={onDashboardChange}
                            dashboard={dashboard}
                            componentTypeToTemplate={componentTypeToTemplate}
                            activeTabId={activeTabId}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              } else {
                return (
                  <div key={index} css={messageContainer}>
                    <div style={{flex: 1}} />
                    <div>
                      <div css={assistantName(theme)}>
                        {currentUser.get("firstName") || ""} {currentUser.get("lastName") || ""}
                      </div>
                      <div css={userMessage(theme)}>{message.get("content")}</div>
                    </div>

                    <div css={userAvatar(theme)}>{userInitials}</div>
                  </div>
                );
              }
            })}

            {isLoading && (
              <div css={messageContainer}>
                <div css={avatar(theme)}>
                  <i className="bhi-magic" />
                </div>
                <div style={{flex: 1}}>
                  <div css={assistantName}>{Branding.aiAssistantName}</div>
                  <div css={messageText(theme)}>
                    <div style={{display: "flex", gap: "4px"}}>
                      <div css={typingDot(theme)} />
                      <div
                        css={[
                          typingDot(theme),
                          css`
                            animation-delay: 0.16s;
                          `
                        ]}
                      />
                      <div
                        css={[
                          typingDot(theme),
                          css`
                            animation-delay: 0.32s;
                          `
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div css={inputSection}>
          <div css={inputContainer}>
            <textarea
              css={textarea}
              placeholder="Begin typing..."
              data-test-id={"ai-assistant-slideout-chatbox"}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (canSend) {
                    handleSendMessage();
                  }
                }
              }}
              rows={1}
            />
            <IconButton
              hoverType="primary"
              icon="arrow-up"
              iconType="bhi"
              style={sendButtonStyle(theme)}
              onClick={handleSendMessage}
              disabled={!canSend}
            />
          </div>
        </div>
      </Drawer>
    );
  }
);

export default AiAssistantDrawer;
